/**
 * The game catalog (games and their tables) is cached on the device each time it loads, so the normal game list
 * and table picker keep working with no connection and offline games play at real tables.
 */
import type { GameSummaryDto, GameTypeDto, TableConfigDto } from '@cardhub/protocol';
import { api, ApiError } from '../api/client';

const KEY = 'th.catalog';

export function cachedCatalog(): GameSummaryDto[] | null {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? 'null') as unknown;
    return Array.isArray(v) && v.length > 0 ? (v as GameSummaryDto[]) : null;
  } catch {
    return null;
  }
}

function store(games: GameSummaryDto[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(games));
  } catch {
    /* storage full or blocked: offline play simply is not available */
  }
}

/** True for failures caused by having no connection to the server (as opposed to the server refusing). */
const unreachable = (e: unknown): boolean => e instanceof ApiError && e.code === 'NETWORK';

/** All games, from the server when reachable (and cached), else from the last cached copy. */
export async function loadGames(): Promise<GameSummaryDto[]> {
  try {
    const games = await api.get<GameSummaryDto[]>('/api/v1/games');
    store(games);
    return games;
  } catch (e) {
    const cached = unreachable(e) ? cachedCatalog() : null;
    if (cached) return cached;
    throw e;
  }
}

export async function loadGame(type: string): Promise<GameSummaryDto> {
  try {
    return await api.get<GameSummaryDto>(`/api/v1/games/${type}`);
  } catch (e) {
    const g = unreachable(e) ? cachedCatalog()?.find((x) => x.type === type) : undefined;
    if (g) return g;
    throw e;
  }
}

/** Refreshes the cached catalog in the background (after sign-in, and whenever the connection returns). */
export function prefetchCatalog(): void {
  if (navigator.onLine) void loadGames().catch(() => undefined);
}

/** A table by id from the cached catalog. */
export function cachedTable(tableConfigId: string): TableConfigDto | null {
  for (const g of cachedCatalog() ?? []) {
    const t = g.tables.find((x) => x.id === tableConfigId);
    if (t) return t;
  }
  return null;
}

/** The table Quick Play would pick for a game (same rule as the server: the cheapest table within 20% of the balance, else the cheapest). */
export function quickPlayTable(gameType: GameTypeDto | string, balance: number): TableConfigDto | null {
  const game = cachedCatalog()?.find((g) => g.type === gameType);
  if (!game || !game.enabled) return null;
  const fits = game.tables.filter((c) => c.status === 'ACTIVE' && c.minBalance <= balance && (c.maxBalance === null || c.maxBalance >= balance));
  if (fits.length === 0) return null;
  const affordable = fits.filter((c) => c.entryFee <= balance * 0.2);
  return [...(affordable.length ? affordable : fits)].sort((a, b) => a.entryFee - b.entryFee)[0]!;
}
